import { Dropdown, RadioButton, TextInput, Button } from '@pmi/dsm-react';
import { Form } from 'react-bootstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { AlertVariants } from '@pmi/dsm-react/dist/types';
import styles from './alertForm.module.scss';
import { useAddCustomAlertMutation } from './apiSlice';
import { accountSelector } from '../../../shared/store/shared/accountSlice';
import { toastWithAnalytics } from '../../../shared/analytics';

const ALERT_MESSAGE_MAX_LENGTH = 232;

const alertOptions: { label: string; value: AlertVariants }[] = [
  {
    label: 'Error',
    value: 'danger',
  },
  {
    label: 'Warning',
    value: 'warning',
  },
  {
    label: 'Success',
    value: 'success',
  },
  {
    label: 'Info',
    value: 'info',
  },
  {
    label: 'Neutral',
    value: 'neutral',
  },
];

interface AlertFormModel {
  alertType: AlertVariants;
  alertMessage: string;
  globalDisplayLocation: boolean;
}

export default function AlertForm() {
  const { currentAccountId } = useSelector(accountSelector);
  const [addCustomAlert, addCustomAlertStatus] = useAddCustomAlertMutation();

  const initialData: AlertFormModel = {
    alertType: null,
    alertMessage: '',
    globalDisplayLocation: false,
  };

  const validationSchema = Yup.object<AlertFormModel>().shape({
    alertType: Yup.string().required('Alert Type is Required'),
    alertMessage: Yup.string()
      .required('Alert Message is Required')
      .max(
        ALERT_MESSAGE_MAX_LENGTH,
        `Alert Message must be max ${ALERT_MESSAGE_MAX_LENGTH} chars`,
      ),
  });

  const formik = useFormik({
    initialValues: initialData,
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: (values: AlertFormModel, helpers) => {
      if (addCustomAlertStatus.isLoading) return;

      addCustomAlert({
        accountId: currentAccountId,
        body: {
          global: values.globalDisplayLocation,
          message: values.alertMessage,
          type: values.alertType,
        },
      })
        .unwrap()
        .then(() => {
          toastWithAnalytics('Alert sent successfully', 'success');
          helpers.resetForm({ values: initialData });
        })
        .catch(() => {
          toastWithAnalytics('Error while adding alert', 'danger');
        });
    },
    validationSchema,
  });

  return (
    <div className={`flex flex-column ${styles.formContainer}`}>
      <div className="row">
        <div className="col-auto">
          <Form.Group controlId="alertType" className="d-flex flex-column">
            <Form.Label>Alert Type</Form.Label>
            <Dropdown
              placeholder="Select"
              options={alertOptions}
              className="alertType"
              onChange={(e, value) => {
                formik.setFieldValue('alertType', value);
                setTimeout(() => formik.validateField('alertType'), 1);
              }}
              onChangeInput={() => {}}
              id="alert-types"
              invalid={!!formik.errors.alertType}
              value={formik.values.alertType}
            />
            <div className="dsm-input__helper-msg mt-2">
              Select the type of alert necessary.
            </div>
            {formik.errors.alertType ? (
              <div className="invalid-feedback">{formik.errors.alertType}</div>
            ) : null}
          </Form.Group>
        </div>
        <div className="col">
          <Form.Group controlId="alertMessage" className="d-flex flex-column">
            <div className="d-flex justify-content-between">
              <Form.Label>Alert Message</Form.Label>
              <div
                className={`input_totalcount ${
                  formik.values.alertMessage?.length > ALERT_MESSAGE_MAX_LENGTH
                    ? 'invalid'
                    : ''
                }`}
              >
                ({formik.values.alertMessage?.length ?? 0}/
                {ALERT_MESSAGE_MAX_LENGTH})
              </div>
            </div>

            <TextInput
              name="alertMessage"
              onChange={formik.handleChange}
              placeholder="What would you like to share?"
              isInvalid={!!formik.errors.alertMessage}
              className={formik.errors.alertMessage ? 'is-invalid' : ''}
              value={formik.values.alertMessage}
              id="alert-message"
            />
            <div className="dsm-input__helper-msg mt-2">
              {ALERT_MESSAGE_MAX_LENGTH} character limit.
            </div>
            {formik.errors.alertMessage ? (
              <div className="invalid-feedback">
                {formik.errors.alertMessage}
              </div>
            ) : null}
          </Form.Group>
        </div>
        <div className="col-auto">
          <Form.Group
            controlId="displayLocation"
            className="d-flex flex-column"
          >
            <Form.Label>Display Location</Form.Label>
            <div className="d-flex">
              <div className="pe-4">
                <RadioButton
                  id="thisAccountLocation"
                  label="This Account"
                  value={false}
                  checked={!formik.values.globalDisplayLocation}
                  name="globalDisplayLocation"
                  onChange={() =>
                    formik.setFieldValue('globalDisplayLocation', false)
                  }
                />
              </div>
              <div>
                <RadioButton
                  id="globalLocation"
                  label="Global"
                  value={true} // eslint-disable-line
                  checked={formik.values.globalDisplayLocation}
                  name="globalDisplayLocation"
                  onChange={() =>
                    formik.setFieldValue('globalDisplayLocation', true)
                  }
                />
              </div>
            </div>
          </Form.Group>
        </div>
      </div>

      <div className="flex mt-4">
        <Button
          variant="primary"
          titleText="Send Alert"
          onClick={() => formik.submitForm()}
          disabled={addCustomAlertStatus.isLoading}
        />
      </div>
    </div>
  );
}
